/**
 * AI Search — standalone tool page. Fires one prompt against multiple LLM
 * platforms in parallel and renders side-by-side answers with citations.
 * Not persisted — this is the "ad-hoc SERP tab" equivalent for GEO.
 */
import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Sparkles, Loader2, ExternalLink, Search as SearchIcon, Gauge } from "lucide-react";
import { toast } from "sonner";

const PLATFORMS = [
  { id: "chat_gpt", label: "ChatGPT", color: "#10a37f" },
  { id: "claude", label: "Claude", color: "#d97706" },
  { id: "gemini", label: "Gemini", color: "#4285f4" },
  { id: "perplexity", label: "Perplexity", color: "#20b8cd" },
];

export default function AISearch() {
  const [prompt, setPrompt] = useState("");
  const [selected, setSelected] = useState(new Set(PLATFORMS.map((p) => p.id)));
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState(null);
  const [totalCost, setTotalCost] = useState(0);
  const [usage, setUsage] = useState(null); // { used, limit, remaining, reset_at, tz }

  const refreshUsage = async () => {
    try {
      const { data } = await api.get("/tools/ai-search/usage");
      setUsage(data);
    } catch {
      /* non-blocking */
    }
  };
  useEffect(() => { refreshUsage(); }, []);

  const togglePlatform = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!prompt.trim()) return;
    if (selected.size === 0) { toast.error("Pick at least one platform"); return; }
    if (usage && usage.remaining <= 0) {
      toast.error(`Daily limit reached (${usage.limit}/day). Resets at midnight ${usage.tz}.`);
      return;
    }
    setRunning(true);
    setResults(null);
    try {
      const { data } = await api.post(`/tools/ai-search`, {
        prompt: prompt.trim(),
        platforms: Array.from(selected),
      });
      setResults(data.results || []);
      setTotalCost(data.total_cost || 0);
      toast.success(`Response ready · $${data.total_cost}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "AI search failed");
    } finally {
      setRunning(false);
      refreshUsage();
    }
  };

  const capped = usage && usage.remaining <= 0;
  const resetLabel = usage?.reset_at
    ? new Date(usage.reset_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : "midnight";

  return (
    <div className="space-y-5 pt-2" data-testid="ai-search-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-[#a0aec0] font-semibold">Tools / AI Search</div>
          <h1 className="font-display text-4xl text-[#1a202c] flex items-center gap-3 mt-1">
            <Sparkles className="h-7 w-7 text-[#4fd1c5]" strokeWidth={2} />
            AI Search Analysis
          </h1>
          <p className="text-sm text-[#718096] mt-1 max-w-2xl">
            Ask any prompt to ChatGPT, Claude, Gemini & Perplexity at once. See what they say, who they cite, and how much each answer cost.
          </p>
        </div>
        {usage && (
          <div
            data-testid="ai-search-usage-badge"
            className={`inline-flex items-center gap-2 rounded-xl border px-3.5 py-2 text-[11px] font-mono-tight ${
              capped
                ? "bg-[#fed7d7]/60 border-[#feb2b2] text-[#c53030]"
                : usage.remaining <= 3
                ? "bg-[#feebcb]/60 border-[#fbd38d] text-[#c05621]"
                : "bg-white border-[#e2e8f0] text-[#4a5568]"
            }`}
            title={capped ? `Cap of ${usage.limit}/day hit. Resets at midnight ${usage.tz}.` : `${usage.remaining} of ${usage.limit} prompts left today (${usage.tz})`}
          >
            <Gauge className="h-3.5 w-3.5" strokeWidth={2.2} />
            <span>
              <span className="font-semibold text-current">{usage.remaining}</span>
              <span className="opacity-70"> / {usage.limit}</span>
              <span className="opacity-70"> left today</span>
            </span>
            <span className="opacity-50">· resets {resetLabel}</span>
          </div>
        )}
      </div>

      <form onSubmit={submit} className="card-soft p-5 space-y-4">
        <div>
          <label className="text-[11px] uppercase tracking-widest text-[#718096] font-bold">Prompt</label>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            maxLength={500}
            rows={3}
            data-testid="ai-search-prompt"
            placeholder='e.g. "best HR consultancy in Surrey" or "outsourced HR pros and cons"'
            className="w-full bg-white border border-[#e2e8f0] rounded-xl px-4 py-3 text-sm mt-1.5 focus:outline-none focus:border-[#4fd1c5] focus:ring-4 focus:ring-[#4fd1c5]/15 transition-all resize-none"
          />
          <div className="text-[10px] text-[#a0aec0] mt-1 text-right">{prompt.length} / 500</div>
        </div>

        <div>
          <div className="text-[11px] uppercase tracking-widest text-[#718096] font-bold mb-2">Platforms</div>
          <div className="flex items-center gap-2 flex-wrap">
            {PLATFORMS.map((p) => {
              const on = selected.has(p.id);
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => togglePlatform(p.id)}
                  data-testid={`ai-search-platform-${p.id}`}
                  style={on ? { background: p.color, borderColor: p.color, color: "white" } : {}}
                  className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-[12px] font-bold uppercase tracking-widest transition-colors ${
                    on ? "" : "bg-white border-[#e2e8f0] text-[#4a5568]"
                  }`}
                >
                  {p.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="text-[11px] text-[#a0aec0]">Web search always on to unlock citations. Each platform runs in parallel.</div>
          <button
            type="submit"
            disabled={running || !prompt.trim() || capped}
            data-testid="ai-search-submit"
            className="btn-primary inline-flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {running ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <SearchIcon className="h-3.5 w-3.5" />}
            {running ? "Running…" : capped ? "Daily limit reached" : "Ask all"}
          </button>
        </div>
      </form>

      {results && (
        <>
          <div className="text-[11px] text-[#a0aec0] font-mono-tight">Total cost this lookup: <span className="text-[#1a202c] font-semibold">${totalCost.toFixed(4)}</span></div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="ai-search-results">
            {results.map((r, i) => {
              const meta = PLATFORMS.find((p) => p.id === r.platform);
              return (
                <div key={i} className="card-soft p-5" data-testid={`ai-search-result-${r.platform}`}>
                  <div className="flex items-center justify-between mb-3">
                    <span
                      style={{ background: `${meta?.color}18`, color: meta?.color, borderColor: `${meta?.color}55` }}
                      className="inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[11px] font-bold uppercase tracking-widest"
                    >
                      {meta?.label || r.platform}
                    </span>
                    <span className="text-[10px] text-[#a0aec0] font-mono-tight">
                      {r.model_name} {r.cost != null && `· $${(r.cost || 0).toFixed(4)}`}
                    </span>
                  </div>
                  {r.error ? (
                    <div className="text-[13px] text-[#c53030]">Error: {r.error}</div>
                  ) : (
                    <>
                      <div className="text-[13px] text-[#4a5568] whitespace-pre-wrap leading-relaxed max-h-96 overflow-y-auto">{r.text || "(no answer)"}</div>
                      {r.sources?.length > 0 && (
                        <>
                          <div className="text-[10px] uppercase tracking-widest text-[#a0aec0] font-bold mt-4 mb-1">
                            Sources ({r.sources.length})
                          </div>
                          <ul className="space-y-1">
                            {r.sources.map((s, si) => (
                              <li key={si} className="text-[12px]">
                                <a href={s.url} target="_blank" rel="noreferrer" className="text-[#2c7a7b] hover:text-[#234e52] underline decoration-dotted inline-flex items-center gap-1 break-all">
                                  <span className="font-mono-tight text-[#a0aec0]">#{si + 1}</span>
                                  {s.title || s.url}
                                  <ExternalLink className="h-2.5 w-2.5" />
                                </a>
                              </li>
                            ))}
                          </ul>
                        </>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
